import { z } from 'zod'
import type { Erc20Transfer } from '../../types'
import { bigintTextSchema, lowerHexSchema } from '../../types/primitive'
import type { TransactionIndex } from '../../types/transactionIndex'
import type { BlockscoutClient } from './client'

type GetTokenTransferEventsByAddressParameters = {
  client: BlockscoutClient
  address: string
  startblock?: number
  endblock?: number
  page?: number
  offset?: number
  sort?: 'asc' | 'desc'
  headers?: Record<string, string>
}

const transferSchema = z.object({
  blockNumber: z.string().regex(/^\d+$/).transform(Number),
  hash: lowerHexSchema,
  timeStamp: z.string().regex(/^\d+$/).transform(Number),
  contractAddress: lowerHexSchema,
  from: lowerHexSchema,
  to: lowerHexSchema,
  logIndex: z.string().regex(/^\d+$/).transform(Number).nullable().optional(),
  tokenDecimal: z
    .union([
      z.literal('').transform(() => undefined),
      z.string().regex(/^\d+$/).transform(Number),
    ])
    .nullable()
    .optional(),
  value: z
    .union([z.literal('').transform(() => undefined), bigintTextSchema])
    .nullable()
    .optional(),
})
const resultSchema = z.array(transferSchema)

/** {@link https://docs.blockscout.com/developer-support/api/rpc-endpoints/account#get-token-transfer-events-by-address} */
export const getTokenTransferEventsByAddress = async ({
  client,
  address,
  startblock = 0,
  endblock,
  page = 1,
  offset = 10_000,
  sort = 'asc',
  headers,
}: GetTokenTransferEventsByAddressParameters): Promise<{
  indexes: readonly TransactionIndex[]
  erc20Transfers: readonly Erc20Transfer[]
}> => {
  try {
    const result = await client.get({
      path: '',
      query: {
        module: 'account',
        action: 'tokentx',
        address,
        startblock: startblock.toString(),
        ...(endblock === undefined
          ? {}
          : {
              endblock: endblock.toString(),
            }),
        page: page.toString(),
        offset: offset.toString(),
        sort,
      },
      resultSchema,
      headers,
    })
    const indexes: TransactionIndex[] = []
    const transfers: Erc20Transfer[] = []
    for (const data of result) {
      indexes.push({
        blockNumber: data.blockNumber,
        hash: data.hash,
        timestamp: data.timeStamp * 1000,
      })
      if (
        data.tokenDecimal !== null &&
        data.tokenDecimal !== undefined &&
        data.value !== null &&
        data.value !== undefined
      ) {
        transfers.push({
          address: data.contractAddress,
          from: data.from,
          logIndex: data.logIndex ?? 0,
          to: data.to,
          value: data.value,
        })
      }
    }
    return {
      indexes,
      erc20Transfers: transfers,
    }
  } catch (error) {
    if (
      error instanceof Error &&
      error.message === 'No token transfers found'
    ) {
      return {
        indexes: [],
        erc20Transfers: [],
      }
    }
    throw error
  }
}
